import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle, RotateCcw } from "lucide-react";

function money(n) {
  const v = Number(n || 0);
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`;
}

function Hint({ text }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" tabIndex={-1} className="inline-flex items-center text-muted-foreground hover:text-foreground">
          <HelpCircle className="w-3 h-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="bg-card border-border max-w-xs text-xs">{text}</TooltipContent>
    </Tooltip>
  );
}

export default function PricingBlock({ form, updateFields, invoiceLocked = false, squareFee = 0 }) {
  const [preview, setPreview] = useState(null);
  const debounceRef = useRef(null);

  const mode = form.pricing_mode || "all_inclusive";
  const nyToNy = form.ny_to_ny !== undefined ? !!form.ny_to_ny : true;
  const isAirport = !!form.is_airport_trip;
  const isCgZone = !!form.is_congestion_zone;

  const driverBaseOverridden = parseFloat(form.driver_base_rate || 0) !== parseFloat(form.base_rate || 0);
  const driverGratOverridden = parseFloat(form.driver_gratuity || 0) !== parseFloat(form.gratuity || 0);
  const anyOverridden = driverBaseOverridden || driverGratOverridden;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const payload = {
          base_rate: parseFloat(form.base_rate) || 0,
          tolls: parseFloat(form.tolls) || 0,
          gratuity: parseFloat(form.gratuity) || 0,
          driver_base_rate: parseFloat(form.driver_base_rate) || 0,
          driver_gratuity: parseFloat(form.driver_gratuity) || 0,
          ny_to_ny: nyToNy,
          is_airport_trip: isAirport,
          is_congestion_zone: isCgZone,
          pricing_mode: mode,
          all_inclusive_amount: parseFloat(form.all_inclusive_amount) || 0,
        };
        const res = await api.post("/jobs/pricing-preview", payload);
        setPreview(res.data);
      } catch (e) { /* ignore transient errors */ }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [form.base_rate, form.tolls, form.gratuity, form.driver_base_rate, form.driver_gratuity,
      nyToNy, isAirport, isCgZone, mode, form.all_inclusive_amount]);

  const p = preview || {};
  const clientFareKey = mode === "all_inclusive" ? "all_inclusive_amount" : "base_rate";
  const isAllInclusive = mode === "all_inclusive";

  // Subtotal: in all-inclusive = fare alone (before tolls/tip additions); in line-items = fare+tolls+tip (before tax/fees)
  const subtotal = isAllInclusive
    ? parseFloat(form[clientFareKey]) || 0
    : (parseFloat(form.base_rate) || 0) + (parseFloat(form.tolls) || 0) + (parseFloat(form.gratuity) || 0);
  const driverSubtotal = isAllInclusive
    ? parseFloat(form.driver_base_rate) || 0
    : (parseFloat(form.driver_base_rate) || 0) + (parseFloat(p.tolls) || 0) + (parseFloat(form.driver_gratuity) || 0);

  return (
    <TooltipProvider delayDuration={200}>
      <div data-testid="pricing-block">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium">Pricing</div>
          {anyOverridden && (
            <button
              type="button"
              onClick={() => updateFields({
                driver_base_rate: parseFloat(form.base_rate) || 0,
                driver_gratuity: parseFloat(form.gratuity) || 0,
              })}
              className="inline-flex items-center gap-1 text-[9px] text-muted-foreground hover:text-foreground"
              data-testid="reset-driver-btn"
            >
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          )}
        </div>

        <div className="rounded-md border border-border overflow-hidden">
          <div className="grid grid-cols-[1fr_1fr] text-[10px] uppercase tracking-wider bg-muted/20">
            <div className="px-3 py-1.5 border-r border-border text-muted-foreground">Client</div>
            <div className="px-3 py-1.5 text-muted-foreground">Driver</div>
          </div>

          {mode === "no_show" ? (
            <>
              {/* NO-SHOW: just two flat amounts. Customer fee + driver fee. No tax/fees/tolls/tip. */}
              <PricingRow label="Fee">
                <NumInput value={form.base_rate} onChange={(v) => updateFields({ base_rate: v })} testId="price-no-show-customer" disabled={invoiceLocked} />
                <NumInput value={form.driver_base_rate} onChange={(v) => updateFields({ driver_base_rate: v })} testId="price-no-show-driver" />
              </PricingRow>
            </>
          ) : isAllInclusive ? (
            <>
              {/* ALL-INCLUSIVE: Fare → computed fees (informational) → Subtotal → Tolls → Tip → Grand Total */}
              <PricingRow label="Fare">
                <NumInput value={form[clientFareKey]} onChange={(v) => updateFields({ [clientFareKey]: v })} testId="price-all-inclusive" disabled={invoiceLocked} />
                <NumInput value={form.driver_base_rate} onChange={(v) => updateFields({ driver_base_rate: v })} testId="price-driver-base" />
              </PricingRow>

              <ComputedRow label="NY Tax" value={p.tax} show={nyToNy} />
              <ComputedRow label="PA Fee" value={p.pa_fee} show={isAirport} />
              <ComputedRow label="CG Fee" value={p.cg_fee} show={isCgZone && nyToNy} />
              <BcfRow mode={mode} p={p} />

              <SubtotalRow clientValue={subtotal} driverValue={driverSubtotal} />

              <PricingRow label={<>Tolls <Hint text="PA $14.79 / Peak $16.79. M-F 6-10a, 4-8p. WND 11a-9p" /></>}>
                <NumInput value={form.tolls} onChange={(v) => updateFields({ tolls: v })} testId="price-tolls" disabled={invoiceLocked} />
                <div className="h-7 px-2 flex items-center text-[10px] text-muted-foreground tabular-nums">{money(p.tolls)}</div>
              </PricingRow>

              <PricingRow label="Tip">
                <NumInput value={form.gratuity} onChange={(v) => updateFields({ gratuity: v })} testId="price-gratuity" disabled={invoiceLocked} />
                <NumInput value={form.driver_gratuity} onChange={(v) => updateFields({ driver_gratuity: v })} testId="price-driver-gratuity" />
              </PricingRow>
            </>
          ) : (
            <>
              {/* LINE ITEMS: Fare → Tolls → Tip → Subtotal → computed fees → Grand Total */}
              <PricingRow label="Fare">
                <NumInput value={form.base_rate} onChange={(v) => updateFields({ base_rate: v })} testId="price-base-rate" disabled={invoiceLocked} />
                <NumInput value={form.driver_base_rate} onChange={(v) => updateFields({ driver_base_rate: v })} testId="price-driver-base" />
              </PricingRow>

              <PricingRow label={<>Tolls <Hint text="PA $14.79 / Peak $16.79. M-F 6-10a, 4-8p. WND 11a-9p" /></>}>
                <NumInput value={form.tolls} onChange={(v) => updateFields({ tolls: v })} testId="price-tolls" disabled={invoiceLocked} />
                <div className="h-7 px-2 flex items-center text-[10px] text-muted-foreground tabular-nums">{money(p.tolls)}</div>
              </PricingRow>

              <PricingRow label="Tip">
                <NumInput value={form.gratuity} onChange={(v) => updateFields({ gratuity: v })} testId="price-gratuity" disabled={invoiceLocked} />
                <NumInput value={form.driver_gratuity} onChange={(v) => updateFields({ driver_gratuity: v })} testId="price-driver-gratuity" />
              </PricingRow>

              <SubtotalRow clientValue={subtotal} driverValue={driverSubtotal} />

              <ComputedRow label="NY Tax" value={p.tax} show={nyToNy} />
              <ComputedRow label="PA Fee" value={p.pa_fee} show={isAirport} />
              <ComputedRow label="CG Fee" value={p.cg_fee} show={isCgZone && nyToNy} />
              <BcfRow mode={mode} p={p} />
            </>
          )}

          <div className="grid grid-cols-[1fr_1fr] text-[10px] border-t-2 border-border/80 bg-card font-semibold">
            <div className="px-3 py-1.5 border-r border-border flex justify-between items-center">
              <span>Grand Total</span>
              <span className="tabular-nums" data-testid="pricing-client-total">{money(p.total)}</span>
            </div>
            <div className="px-3 py-1.5 flex justify-between items-center">
              <span>Grand Total</span>
              <span className="tabular-nums text-primary" data-testid="pricing-driver-total">{money(p.driver_total)}</span>
            </div>
          </div>

          {squareFee > 0 && (
            <div className="grid grid-cols-[1fr_1fr] text-[10px] min-h-[28px]">
              <div className="px-3 py-1 border-r border-border flex justify-between items-center">
                <span className="text-muted-foreground whitespace-nowrap">Sq Fee</span>
                <span className="tabular-nums text-red-400" data-testid="pricing-sq-fee">-{money(squareFee)}</span>
              </div>
              <div className="px-3 py-1" />
            </div>
          )}

          <NetRow p={p} mode={mode} squareFee={squareFee} />
        </div>
      </div>
    </TooltipProvider>
  );
}

function NetRow({ p, mode, squareFee = 0 }) {
  const total = parseFloat(p.total) || 0;
  const driverTotal = parseFloat(p.driver_total) || 0;
  const tax = parseFloat(p.tax) || 0;
  const paFee = parseFloat(p.pa_fee) || 0;
  const cgFee = parseFloat(p.cg_fee) || 0;
  const bcf = parseFloat(mode === "line_items" ? p.bcf_charge : p.bcf_liability) || 0;
  const remittances = tax + paFee + cgFee + bcf;
  const sqFee = parseFloat(squareFee) || 0;
  const net = total - driverTotal - remittances - sqFee;
  const positive = net >= 0;
  const parts = [`${money(total)} client total`, `−${money(driverTotal)} driver`, `−${money(remittances)} remittances`];
  if (sqFee > 0) parts.push(`−${money(sqFee)} Sq fee`);
  parts.push(`= ${money(net)}`);
  const tooltipText = mode === "no_show"
    ? `${money(total)} customer fee · −${money(driverTotal)} driver${sqFee > 0 ? ` · −${money(sqFee)} Sq fee` : ""} = ${money(net)}`
    : parts.join(" · ");
  return (
    <div
      className={`border-t-2 border-border px-3 py-2 flex items-center justify-between text-[11px] font-semibold ${positive ? "bg-emerald-500/5" : "bg-red-500/5"}`}
      data-testid="pricing-net-row"
    >
      <span className="uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
        Net
        <Hint text={tooltipText} />
      </span>
      <span className={`tabular-nums text-sm ${positive ? "text-emerald-400" : "text-red-400"}`} data-testid="pricing-net-value">
        {money(net)}
      </span>
    </div>
  );
}

function SubtotalRow({ clientValue, driverValue }) {
  return (
    <div className="grid grid-cols-[1fr_1fr] text-[10px] min-h-[28px] border-t border-border/50">
      <div className="px-3 py-1 border-r border-border flex justify-between items-center">
        <span className="text-muted-foreground font-medium">Subtotal</span>
        <span className="tabular-nums text-muted-foreground font-medium">{money(clientValue)}</span>
      </div>
      <div className="px-3 py-1 flex justify-between items-center">
        <span className="text-muted-foreground/40 font-medium">Subtotal</span>
        <span className="tabular-nums text-muted-foreground/40 font-medium">{money(driverValue)}</span>
      </div>
    </div>
  );
}

function BcfRow({ mode, p }) {
  return (
    <div className="grid grid-cols-[1fr_1fr] text-[10px] bg-muted/5 min-h-[32px]">
      <div className="px-3 py-1 border-r border-border flex justify-between items-center overflow-hidden">
        <span className="flex items-center gap-1 whitespace-nowrap">
          BCF
          <Hint text={mode === "line_items"
            ? "Charged to customer at 2.5%. You remit 2.44% to NYS BCF."
            : "Set aside 2.44% of implied base for quarterly BCF filing."} />
        </span>
        <span className="tabular-nums text-muted-foreground">{money(mode === "line_items" ? p.bcf_charge : p.bcf_liability)}</span>
      </div>
      <div className="px-3 py-1 overflow-hidden flex justify-between items-center">
        <span className="text-muted-foreground/40 whitespace-nowrap">BCF</span>
        <span className="text-muted-foreground/40">—</span>
      </div>
    </div>
  );
}

function PricingRow({ label, children }) {
  const [clientEl, driverEl] = Array.isArray(children) ? children : [children, null];
  return (
    <div className="grid grid-cols-[1fr_1fr] text-[10px] min-h-[32px]">
      <div className="px-3 py-1 border-r border-border flex items-center gap-2 justify-between overflow-hidden">
        <Label className="text-[10px] text-muted-foreground flex items-center gap-1 whitespace-nowrap">{label}</Label>
        <div className="w-[68px] flex-shrink-0">{clientEl}</div>
      </div>
      <div className="px-3 py-1 flex items-center gap-2 justify-between overflow-hidden">
        <Label className="text-[10px] text-muted-foreground/40 whitespace-nowrap">{label}</Label>
        <div className="w-[68px] flex-shrink-0">{driverEl}</div>
      </div>
    </div>
  );
}

function ComputedRow({ label, value, show }) {
  if (!show) return null;
  return (
    <div className="grid grid-cols-[1fr_1fr] text-[10px] min-h-[32px]">
      <div className="px-3 py-1 border-r border-border flex justify-between items-center overflow-hidden">
        <span className="text-muted-foreground whitespace-nowrap">{label}</span>
        <span className="tabular-nums text-muted-foreground">{money(value)}</span>
      </div>
      <div className="px-3 py-1 overflow-hidden flex justify-between items-center">
        <span className="text-muted-foreground/40 whitespace-nowrap">{label}</span>
        <span className="text-muted-foreground/40">—</span>
      </div>
    </div>
  );
}

function NumInput({ value, onChange, testId, disabled = false }) {
  return (
    <div className="relative">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">$</span>
      <Input
        type="number" min={0} step={0.01}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`bg-muted/20 border-border h-7 text-[10px] pl-5 pr-1 text-left ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        data-testid={testId}
      />
    </div>
  );
}
